import { useState } from "react";
import { FEATURE_GROUPS, PossiblePackage } from "lhc_permissions";
import { GenerateLoadingOrError } from "../../lib/utils";
import { useListUsers, DbUserWithPackages as User, useUpdateUserPermissions } from "../../lib/api/users";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { useToast } from "@/components/ui/use-toast";
import { hasAccessToFeature } from "@/lib/models/auth";
import { Plus } from "lucide-react";

interface FeatureRow {
  id: string;
  feature_group_name: string;
  feature_name: string;
  feature: {
    name: string;
    requiredPackages: PossiblePackage[];
    requiredPermissions?: string[];
  };
}

const userColumns: DataTableColumn<User>[] = [
  {
    id: "name",
    accessorKey: "name",
    header: "Name",
    cell: (info) => info.getValue() as string,
  },
  {
    id: "email",
    accessorKey: "email",
    header: "Email",
    cell: (info) => info.getValue() as string,
  },
  {
    id: "role",
    accessorKey: "role",
    header: "Role",
    cell: (info) => info.getValue() as string,
  },
];

export function ManagePermissionsPage() {
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);
  const usersRes = useListUsers();

  const loadingOrErrorUi = GenerateLoadingOrError([usersRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const users = usersRes?.data?.data || [];
  const selectedUser = users?.find((user: typeof users[number]) => user.email === selectedUserEmail) ?? null;

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl mb-6">
        Manage Permissions
      </h1>

      <DataTable
        data={users}
        columns={userColumns}
        enableSorting
        enableFiltering
        enableColumnResizing
        enableColumnVisibility
        enableMultiSelect
        enableMultiFilter
        enableMultiSort
        enableGrouping
        useSteppedGroupingExpansion
        filterStyle="select"
        filterMode="internal"
        stickyHeaders
        rowClickable
        onRowClick={(row) => setSelectedUserEmail(row.email)}
        emptyMessage="No users found"
        defaultSorting={[{ id: "name", desc: false }]}
      />

      <UserPermissionsDialog
        user={selectedUser}
        onClose={() => setSelectedUserEmail(null)}
      />
    </section>
  );
}

function UserPermissionsDialog({ user, onClose }: { user: User | null; onClose: () => void }) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateUserPermissions = useUpdateUserPermissions();

  if (!user) return null;

  const featureData = FEATURE_GROUPS.reduce(
    (acc, feature_group) => {
      feature_group.features.forEach((feature) => {
        const row: FeatureRow = {
          id: `${feature_group.name}|${feature.name}`,
          feature_group_name: feature_group.name,
          feature_name: feature.name,
          feature,
        };

        if (hasAccessToFeature(feature, { trusted_metadata: { packages: user.packages, role: user.role } })) {
          acc.accessible.push(row);
        } else if (!feature.skipPermissionable) {
          acc.missing.push(row);
        }
      });
      return acc;
    },
    { accessible: [] as FeatureRow[], missing: [] as FeatureRow[] },
  );

  featureData.accessible.sort(
    (a, b) => a.feature_group_name.localeCompare(b.feature_group_name) || a.feature_name.localeCompare(b.feature_name),
  );
  featureData.missing.sort(
    (a, b) => a.feature_group_name.localeCompare(b.feature_group_name) || a.feature_name.localeCompare(b.feature_name),
  );

  const handleAddFeature = async (featureRow: FeatureRow) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const requested_feature = featureRow.feature;
      const requested_packages = (requested_feature.requiredPackages || []).map((a_package: string) => {
        const newPermissionTypes = requested_feature.requiredPermissions || [];
        const existingPermissionTypes = user.packages.find(pkg => pkg.split(":")[0] === a_package)?.split(":")?.[1] || [];
        const existingAndNewPermissionTypes = Array.from(new Set([...newPermissionTypes, existingPermissionTypes]));
        return [a_package, existingAndNewPermissionTypes?.join("")].filter(Boolean).join(":");
      });
      const packagesToFilterOut = (requested_feature.requiredPackages || []).map(a_package => a_package as string);
      const new_permissions = Array.from(
        new Set([
          ...user.packages.filter((existingPackage: string) => !packagesToFilterOut.includes(existingPackage.split(":")[0])),
          ...requested_packages,
        ]),
      );

      const result = await updateUserPermissions({
        user_id: user.id,
        permissions: new_permissions,
      });

      if (!result.success) {
        toast({
          title: "Failed to add feature",
          description: result.message || "An unknown error occurred",
        });
      } else {
        toast({
          title: "Added",
          description: `${featureRow.feature_group_name}: ${featureRow.feature_name}`,
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An unknown error occurred";
      toast({
        title: "Failed to add feature",
        description: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentColumns: DataTableColumn<FeatureRow>[] = [
    {
      id: "feature_group_name",
      accessorKey: "feature_group_name",
      header: "Feature Group",
      cell: (info) => info.getValue() as string,
      size: 130,
    },
    {
      id: "feature_name",
      accessorKey: "feature_name",
      header: "Feature",
      cell: (info) => info.getValue() as string,
      size: 160,
    },
  ];

  const missingColumns: DataTableColumn<FeatureRow>[] = [
    {
      id: "actions",
      header: "Actions",
      disableRowClick: true,
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline"
          disabled={isSubmitting}
          onClick={() => handleAddFeature(row.original)}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      ),
    },
    {
      id: "feature_group_name",
      accessorKey: "feature_group_name",
      header: "Feature Group",
      cell: (info) => info.getValue() as string,
      size: 130,
    },
    {
      id: "feature_name",
      accessorKey: "feature_name",
      header: "Feature",
      cell: (info) => info.getValue() as string,
      size: 160,
    },
  ];

  return (
    <Dialog open={!!user} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{user.name}&apos;s Permissions</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-hidden">
          <div className="flex flex-col min-h-0">
            <h3 className="font-semibold mb-2">Current Permissions</h3>
            <div className="flex-1 min-h-0 overflow-auto">
              <DataTable
                data={featureData.accessible}
                columns={currentColumns}
                enableColumnPinning
                enableColumnResizing
                useSteppedGroupingExpansion
                enableSorting
                filterStyle="select"
                filterMode="internal"
                stickyHeaders
                emptyMessage="No permissions granted"
                defaultSorting={[{ id: "feature_group_name", desc: false }, { id: "feature_name", desc: false }]}
              />
            </div>
          </div>

          <div className="flex flex-col min-h-0">
            <h3 className="font-semibold mb-2">Missing Permissions</h3>
            <div className="flex-1 min-h-0 overflow-auto">
              <DataTable
                data={featureData.missing}
                columns={missingColumns}
                enableColumnPinning
                enableColumnResizing
                useSteppedGroupingExpansion
                enableSorting
                filterStyle="select"
                filterMode="internal"
                stickyHeaders
                emptyMessage="All permissions granted"
                defaultSorting={[{ id: "feature_group_name", desc: false }, { id: "feature_name", desc: false }]}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ManagePermissionsPage;
